import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import Banner from "../Components/Banner";
import { donationPageData } from "../Constants/data.jsx";
import { Helmet } from "react-helmet-async";
import { Loader } from "@googlemaps/js-api-loader";
import axiosInstance from "../api/axiosConfig";
import PayPalModal from "../Components/PayPalDonationModal.jsx";
import StripeElementsModal from "../Components/StripeElementsModal.jsx";

const AUSTRALIA_CODE = "AU";

const INITIAL_FORM_DATA = {
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  street_no: "",
  street_name: "",
  suburb: "",
  state: "",
  country: AUSTRALIA_CODE,
  postal_code: "",
  donation_for: "",
  donation_details: "",
  amount: "",
  donation_mode: "",
};

// Helper function to mask text (show first 3 chars, mask rest with asterisks)
const maskText = (text) => {
  if (!text) return "";
  if (text.length <= 3) return text;
  const firstThree = text.substring(0, 3);
  const maskedPart = "*".repeat(text.length - 3);
  return firstThree + maskedPart;
};

// Helper function to mask email specially (show first 3 chars before @, mask rest)
const maskEmail = (email) => {
  if (!email) return "";
  const atIndex = email.indexOf("@");
  if (atIndex === -1) return maskText(email);

  const username = email.substring(0, atIndex);
  const domain = email.substring(atIndex);

  if (username.length <= 3) {
    return username + domain;
  }

  const firstThree = username.substring(0, 3);
  const maskedUsername = firstThree + "*".repeat(username.length - 3);
  return maskedUsername + domain;
};

// Helper function to mask phone number (show first 3 digits, mask rest)
const maskPhoneNumber = (phone) => {
  if (!phone) return "";
  if (phone.length <= 3) return phone;
  const firstThree = phone.substring(0, 3);
  const maskedPart = "*".repeat(phone.length - 3);
  return firstThree + maskedPart;
};

// Helper function to mask amount
const maskAmount = (amount) => {
  if (!amount) return "";
  const amountStr = amount.toString();
  if (amountStr.length <= 3) return amountStr;
  const firstThree = amountStr.substring(0, 3);
  const maskedPart = "*".repeat(amountStr.length - 3);
  return firstThree + maskedPart;
};

// Helper function to mask address components (show first 4 chars, mask rest)
const maskAddressComponent = (text) => {
  if (!text) return "";
  if (text.length <= 4) return text;
  const firstFour = text.substring(0, 4);
  const maskedPart = "*".repeat(text.length - 4);
  return firstFour + maskedPart;
};

// Helper function to mask full address string while preserving format
const maskFullAddress = (address) => {
  if (!address) return "";

  const components = address.split(",").map((comp) => comp.trim());

  const maskedComponents = components.map((component) => {
    const words = component.split(/\s+/);
    const maskedWords = words.map((word) => {
      if (word.length <= 3) return word;
      return word.substring(0, 3) + "*".repeat(word.length - 3);
    });
    return maskedWords.join(" ");
  });

  return maskedComponents.join(", ");
};

const Donation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const alertTimeoutRef = useRef(null);
  const currentEmailRef = useRef("");
  const isManualClearingRef = useRef(false);
  const isPopulatingRef = useRef(false);

  const getInitialFormData = () => {
    const preservedData = location.state?.preservedFormData;
    if (preservedData && location.state?.preserveData) {
      return {
        ...INITIAL_FORM_DATA,
        ...preservedData,
        donation_mode: preservedData.donation_mode || "",
      };
    }
    return INITIAL_FORM_DATA;
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const [displayData, setDisplayData] = useState(getInitialFormData());
  const [loading, setLoading] = useState(false);
  const [donationResponse, setDonationResponse] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertType, setAlertType] = useState(null);
  const [addressError, setAddressError] = useState("");

  const [isLookingUpDonor, setIsLookingUpDonor] = useState(false);
  const [donorFound, setDonorFound] = useState(false);
  const [donorStats, setDonorStats] = useState(null);
  const [isExistingDonor, setIsExistingDonor] = useState(false);

  const [isPayPalModalOpen, setIsPayPalModalOpen] = useState(false);
  const [pendingPayPalData, setPendingPayPalData] = useState(null);

  const [isStripeModalOpen, setIsStripeModalOpen] = useState(false);
  const [pendingStripeData, setPendingStripeData] = useState(null);

  useEffect(() => {
    const preservedData = location.state?.preservedFormData;
    if (
      preservedData &&
      location.state?.preserveData &&
      addressInputRef.current
    ) {
      const addressParts = [];
      if (preservedData.street_name) addressParts.push(preservedData.street_name);
      if (preservedData.suburb) addressParts.push(preservedData.suburb);
      if (preservedData.state) addressParts.push(preservedData.state);
      if (preservedData.country) addressParts.push(preservedData.country);
      if (preservedData.postal_code) addressParts.push(preservedData.postal_code);

      if (addressParts.length > 0) {
        addressInputRef.current.value = addressParts.join(", ");
      }
    }

    if (location.state?.preserveData) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    let isMounted = true;

    const initGoogleAutocomplete = async () => {
      try {
        const loader = new Loader({
          apiKey: "AIzaSyBrMyjMAzcSM6OrUdab4wKm5CJ3OhqLO2o",
          version: "weekly",
          libraries: ["places"],
        });

        await loader.load();

        if (!isMounted || !window.google || !addressInputRef.current) return;

        if (window.google.maps.places.Autocomplete) {
          autocompleteRef.current = new window.google.maps.places.Autocomplete(
            addressInputRef.current,
            {
              types: ["address"],
              componentRestrictions: { country: "au" },
              fields: ["address_components", "formatted_address"],
            }
          );

          autocompleteRef.current.addListener("place_changed", () => {
            const place = autocompleteRef.current.getPlace();
            handlePlaceSelect(place);
          });
        } else {
          setAddressError(
            "Address autocomplete is unavailable. Please enter your address manually."
          );
        }
      } catch (error) {
        console.error("Google autocomplete failed:", error);
        setAddressError(
          "Address autocomplete is unavailable. Please enter your address manually."
        );
      }
    };

    initGoogleAutocomplete();

    return () => {
      isMounted = false;
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
      if (alertTimeoutRef.current) {
        clearTimeout(alertTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setDisplayData({
      ...formData,
      first_name: isExistingDonor ? maskText(formData.first_name) : formData.first_name,
      last_name: isExistingDonor ? maskText(formData.last_name) : formData.last_name,
      email: isExistingDonor ? maskEmail(formData.email) : formData.email,
      phone_number: isExistingDonor
        ? maskPhoneNumber(formData.phone_number)
        : formData.phone_number,
      street_no: isExistingDonor
        ? maskAddressComponent(formData.street_no)
        : formData.street_no,
      street_name: isExistingDonor
        ? maskAddressComponent(formData.street_name)
        : formData.street_name,
      suburb: isExistingDonor ? maskAddressComponent(formData.suburb) : formData.suburb,
      state: isExistingDonor ? maskAddressComponent(formData.state) : formData.state,
      postal_code: isExistingDonor
        ? maskAddressComponent(formData.postal_code)
        : formData.postal_code,
      donation_for: isExistingDonor
        ? maskText(formData.donation_for)
        : formData.donation_for,
      donation_details: isExistingDonor
        ? maskText(formData.donation_details)
        : formData.donation_details,
      amount: isExistingDonor ? maskAmount(formData.amount) : formData.amount,
    });
  }, [formData, isExistingDonor]);

  const clearFormFields = (keepEmail = false) => {
    isManualClearingRef.current = true;

    setFormData((prev) => ({
      ...prev,
      first_name: "",
      last_name: "",
      phone_number: "",
      street_no: "",
      street_name: "",
      suburb: "",
      state: "",
      postal_code: "",
      donation_for: "",
      donation_details: "",
      amount: "",
      email: keepEmail ? prev.email : "",
      country: AUSTRALIA_CODE,
      donation_mode: prev.donation_mode,
    }));

    if (addressInputRef.current) {
      addressInputRef.current.value = "";
    }

    setDonorFound(false);
    setDonorStats(null);
    setIsExistingDonor(false);

    setTimeout(() => {
      isManualClearingRef.current = false;
    }, 200);
  };

  const populateFormWithDonorData = (donor, address) => {
    isPopulatingRef.current = true;

    const addressParts = [];
    if (address?.street_name) addressParts.push(address.street_name);
    if (address?.suburb) addressParts.push(address.suburb);
    if (address?.state) addressParts.push(address.state);
    if (address?.country) addressParts.push(address.country);
    if (address?.postal_code) addressParts.push(address.postal_code);

    const fullAddressString = addressParts.join(", ");
    const maskedFullAddress = maskFullAddress(fullAddressString);

    setFormData((prev) => ({
      ...prev,
      first_name: donor.first_name || "",
      last_name: donor.last_name || "",
      phone_number: donor.phone_number || "",
      email: donor.email || prev.email,
      street_name: address?.street_name || "",
      suburb: address?.suburb || "",
      state: address?.state || "",
      postal_code: address?.postal_code || "",
      country: address?.country || AUSTRALIA_CODE,
    }));

    if (addressInputRef.current) {
      addressInputRef.current.value = maskedFullAddress;
    }

    setTimeout(() => {
      isPopulatingRef.current = false;
    }, 200);
  };

  const lookupDonorByEmail = async (email) => {
    if (!email || !email.includes("@") || email.length < 5) {
      clearFormFields(true);
      setIsExistingDonor(false);
      return;
    }

    if (isManualClearingRef.current) {
      return;
    }

    setIsLookingUpDonor(true);

    try {
      const response = await axiosInstance.post("/donations/donor/by-email", {
        email: email,
      });

      if (response.data.success && response.data.data.donor) {
        const donor = response.data.data.donor;
        const address = donor.address || {};

        populateFormWithDonorData(donor, address);

        setDonorStats(donor.stats);
        setDonorFound(true);
        setIsExistingDonor(true);

        currentEmailRef.current = email;

        showAlert(
          `Welcome back ${donor.first_name}! Your details have been loaded. You can edit any field if needed.`,
          "success"
        );
      } else {
        clearFormFields(true);
        setDonorFound(false);
        setDonorStats(null);
        setIsExistingDonor(false);
        showAlert("New donor - Please fill in your details", "info");
      }
    } catch (error) {
      clearFormFields(true);
      setDonorFound(false);
      setDonorStats(null);
      setIsExistingDonor(false);

      if (error.response?.status !== 404) {
        console.error("Donor lookup error:", error);
        showAlert(
          "Could not check donor status. Please fill in your details.",
          "warning"
        );
      } else {
        showAlert("New donor - Please fill in your details", "info");
      }
    } finally {
      setIsLookingUpDonor(false);
    }
  };

  const handlePlaceSelect = (place) => {
    if (!place || !place.address_components) return;

    const getComponent = (type) => {
      const component = place.address_components.find((c) => c.types.includes(type));
      return component ? component.long_name : "";
    };

    const streetNumber = getComponent("street_number");
    const route = getComponent("route");
    const suburb =
      getComponent("locality") ||
      getComponent("sublocality") ||
      getComponent("sublocality_level_1");
    const state = getComponent("administrative_area_level_1");
    const postalCode = getComponent("postal_code");
    const country = getComponent("country");

    const fullStreetName =
      streetNumber && route
        ? `${streetNumber} ${route}`
        : route || streetNumber || "";

    setFormData((prev) => ({
      ...prev,
      street_no: streetNumber,
      street_name: fullStreetName,
      suburb,
      state,
      country: country || AUSTRALIA_CODE,
      postal_code: postalCode,
    }));

    setAddressError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (isPopulatingRef.current) {
      return;
    }

    if (name === "email") {
      if (isExistingDonor && value !== formData.email) {
        setIsExistingDonor(false);
      }

      setFormData((prev) => ({
        ...prev,
        email: value,
      }));

      if (value !== currentEmailRef.current) {
        setDonorFound(false);
        setDonorStats(null);
        currentEmailRef.current = value;
      }
      return;
    }

    if (name === "address_input") {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEmailBlur = () => {
    const email = formData.email;

    if (email && email.includes("@") && email.length >= 5) {
      if (email !== currentEmailRef.current || (!donorFound && !isExistingDonor)) {
        lookupDonorByEmail(email);
      }
    } else if (!email || email === "") {
      clearFormFields(false);
    }
  };

  const resetForm = () => {
    clearFormFields(false);
    setFormData((prev) => ({
      ...prev,
      donation_mode: "",
    }));
    currentEmailRef.current = "";
    setIsExistingDonor(false);
  };

  const showAlert = (message, type = "success") => {
    setAlertMessage(message);
    setAlertType(type);

    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
    }

    alertTimeoutRef.current = setTimeout(() => {
      setAlertMessage(null);
      setAlertType(null);
    }, 5000);
  };

  const validateAddress = () => {
    return true;
  };

  const handlePayPalSuccess = async (paymentDetails) => {
    showAlert(`Payment successful!`, "success");

    try {
      await axiosInstance.patch(`/donations/${paymentDetails.transactionId}`, {
        status: "completed",
        payment_details: paymentDetails,
      });

      sessionStorage.setItem(
        "pendingDonation",
        JSON.stringify({
          transaction_id: paymentDetails.transactionId,
          amount: paymentDetails.amount,
          status: "completed",
        })
      );

      resetForm();

      setTimeout(() => {
        setIsPayPalModalOpen(false);
        setPendingPayPalData(null);
        navigate("/donation-success?transaction_id=" + paymentDetails.transactionId);
      }, 2000);
    } catch (error) {
      console.error("Failed to update donation status:", error);
      showAlert(
        "Payment successful but status update failed. Please contact support.",
        "warning"
      );

      setTimeout(() => {
        setIsPayPalModalOpen(false);
        setPendingPayPalData(null);
        navigate("/donation-success?transaction_id=" + paymentDetails.transactionId);
      }, 2000);
    }
  };

  const handlePayPalError = (errorMessage) => {
    showAlert(errorMessage, "error");
    setIsPayPalModalOpen(false);
    setPendingPayPalData(null);
  };

  const handlePayPalCancel = (cancelData) => {
    showAlert("Payment was cancelled", "info");
    setIsPayPalModalOpen(false);

    navigate("/donation-cancel", {
      state: {
        formData: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone_number: formData.phone_number,
          street_no: formData.street_no,
          street_name: formData.street_name,
          suburb: formData.suburb,
          state: formData.state,
          postal_code: formData.postal_code,
          donation_for: formData.donation_for,
          donation_details: formData.donation_details,
          amount: formData.amount,
          donation_mode: formData.donation_mode,
        },
        transactionId: cancelData?.transactionId,
      },
      replace: true,
    });

    setPendingPayPalData(null);
  };

  const handlePayPalClose = () => {
    setIsPayPalModalOpen(false);
    setPendingPayPalData(null);
  };

  const handleStripeSuccess = async (paymentDetails) => {
    showAlert(
      `Payment successful! Transaction ID: ${paymentDetails.transactionId}`,
      "success"
    );

    sessionStorage.setItem(
      "pendingDonation",
      JSON.stringify({
        transaction_id: paymentDetails.transactionId,
        amount: paymentDetails.amount,
        status: "completed",
      })
    );

    resetForm();

    setTimeout(() => {
      setIsStripeModalOpen(false);
      setPendingStripeData(null);
      navigate("/donation-success?transaction_id=" + paymentDetails.transactionId);
    }, 2000);
  };

  const handleStripeError = (errorMessage) => {
    showAlert(errorMessage, "error");
    setIsStripeModalOpen(false);
    setPendingStripeData(null);
  };

  const handleStripeCancel = (cancelData) => {
    showAlert("Payment was cancelled", "info");
    setIsStripeModalOpen(false);

    navigate("/donation-cancel", {
      state: {
        formData: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone_number: formData.phone_number,
          street_no: formData.street_no,
          street_name: formData.street_name,
          suburb: formData.suburb,
          state: formData.state,
          postal_code: formData.postal_code,
          donation_for: formData.donation_for,
          donation_details: formData.donation_details,
          amount: formData.amount,
          donation_mode: formData.donation_mode,
        },
        transactionId: cancelData?.transactionId,
      },
      replace: true,
    });

    setPendingStripeData(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAlertMessage(null);

    if (!formData.donation_mode) {
      showAlert("Please select a payment method (Stripe or Bank Transfer)", "warning");
      setLoading(false);
      return;
    }

    const amountNumber = parseFloat(formData.amount);

    if (Number.isNaN(amountNumber) || amountNumber <= 0) {
      showAlert("Please enter a valid donation amount", "warning");
      setLoading(false);
      return;
    }

    if (formData.donation_mode === "stripe") {
      try {
        const donationData = {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim(),
          phone_number: formData.phone_number.trim(),
          street_name: formData.street_name?.trim() || "",
          suburb: formData.suburb?.trim() || "",
          state: formData.state?.trim() || "",
          country: formData.country,
          postal_code: formData.postal_code?.trim() || "",
          donation_for: formData.donation_for.trim(),
          donation_details: formData.donation_details.trim(),
          amount: amountNumber,
        };

        console.log("Creating Stripe Payment Intent:", donationData);

        const response = await axiosInstance.post(
          "/donations/stripe/payment-intent",
          donationData
        );

        console.log("Stripe response:", response.data);

        if (response.data.success) {
          const data = response.data.data;

          sessionStorage.setItem(
            "pendingDonation",
            JSON.stringify({
              transaction_id: data.transaction_id,
              amount: data.amount,
              donation_for: formData.donation_for,
            })
          );

          setDonationResponse(data.transaction);

          setPendingStripeData({
            amount: amountNumber,
            transactionId: data.transaction_id,
            donorEmail: formData.email,
            donorName: formData.first_name + " " + formData.last_name,
            clientSecret: data.client_secret,
            publishableKey: data.publishable_key,
          });

          setTimeout(() => {
            setIsStripeModalOpen(true);
            setLoading(false);
          }, 500);
        } else {
          throw new Error(response.data.message || "Failed to create donation");
        }
      } catch (error) {
        console.error("Stripe API Error:", error);

        if (error.response?.data?.errors) {
          const errors = Object.values(error.response.data.errors).flat().join(", ");
          showAlert(errors, "error");
        } else if (error.response?.data?.message) {
          showAlert(error.response.data.message, "error");
        } else if (error.message === "Network Error") {
          showAlert(
            "Cannot connect to server. Please check your connection.",
            "error"
          );
        } else {
          showAlert("An unexpected error occurred. Please try again.", "error");
        }

        setLoading(false);
      }
    } else if (formData.donation_mode === "paypal") {
      try {
        const donationData = {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim(),
          phone_number: formData.phone_number.trim(),
          street_name: formData.street_name?.trim() || "",
          suburb: formData.suburb?.trim() || "",
          state: formData.state?.trim() || "",
          country: formData.country,
          postal_code: formData.postal_code?.trim() || "",
          donation_for: formData.donation_for.trim(),
          donation_details: formData.donation_details.trim(),
          amount: amountNumber,
          donation_mode: "paypal",
        };

        const response = await axiosInstance.post("/donations", donationData);
        const donation = response.data.data;

        setDonationResponse(donation.transaction);

        showAlert(
          `Donation created! Transaction ID: ${donation.transaction.transaction_id}`,
          "success"
        );

        setPendingPayPalData({
          amount: amountNumber,
          transactionId: donation.transaction.transaction_id,
          donorEmail: formData.email,
        });

        setTimeout(() => {
          setIsPayPalModalOpen(true);
          setLoading(false);
        }, 500);
      } catch (error) {
        console.error("PayPal API Error:", error);

        if (error.response?.data?.errors) {
          const errors = Object.values(error.response.data.errors).flat().join(", ");
          showAlert(errors, "error");
        } else {
          showAlert(error.response?.data?.message || "An error occurred", "error");
        }
        setLoading(false);
      }
    } else if (formData.donation_mode === "bank_transfer") {
      try {
        const donationData = {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim(),
          phone_number: formData.phone_number.trim(),
          street_name: formData.street_name?.trim() || "",
          suburb: formData.suburb?.trim() || "",
          state: formData.state?.trim() || "",
          country: formData.country,
          postal_code: formData.postal_code?.trim() || "",
          donation_for: formData.donation_for.trim(),
          donation_details: formData.donation_details.trim(),
          amount: amountNumber,
          donation_mode: "bank_transfer",
        };

        const response = await axiosInstance.post("/donations", donationData);
        const donation = response.data.data;

        setDonationResponse(donation.transaction);

        showAlert(
          `Bank Transfer Instructions:\nPlease transfer $${amountNumber.toFixed(
            2
          )} to:\nBank: CBA\nBSB: 062-268\nAccount: 10465995\nReference: ${
            donation.transaction.transaction_id
          }`,
          "info"
        );

        setLoading(false);
      } catch (error) {
        console.error("Bank Transfer API Error:", error);
        showAlert(error.response?.data?.message || "An error occurred", "error");
        setLoading(false);
      }
    }
  };

  const Alert = ({ message, type }) => {
    const alertClasses = {
      success: "alert-success",
      error: "alert-danger",
      warning: "alert-warning",
      info: "alert-info",
    };

    const icons = {
      success: "bi-check-circle-fill",
      error: "bi-exclamation-triangle-fill",
      warning: "bi-exclamation-circle-fill",
      info: "bi-info-circle-fill",
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`alert ${alertClasses[type]} alert-dismissible fade show position-fixed top-0 start-50 translate-middle-x mt-4 shadow-lg`}
        style={{ zIndex: 9999, maxWidth: "600px", width: "calc(100% - 2rem)" }}
        role="alert"
      >
        <div className="d-flex align-items-center">
          <i className={`bi ${icons[type]} me-2 fs-5`}></i>
          <span style={{ whiteSpace: "pre-line" }}>{message}</span>
        </div>
        <button
          type="button"
          className="btn-close"
          onClick={() => {
            setAlertMessage(null);
            setAlertType(null);
          }}
          aria-label="Close"
        />
      </motion.div>
    );
  };

  const DonorStatsCard = ({ stats }) => {
    if (!stats) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="card bg-light border-0 rounded-4 mt-3"
      >
        <div className="card-body p-3">
          <h6 className="fw-bold mb-3 heading">
            <i className="bi bi-graph-up-arrow text-warning me-2"></i>
            Your Donation History
          </h6>
          <div className="row g-2">
            <div className="col-6">
              <small className="text-muted d-block">Total Donations</small>
              <strong>{stats.total_donations || 0}</strong>
            </div>
            <div className="col-6">
              <small className="text-muted d-block">Completed</small>
              <strong>{stats.completed_donations || 0}</strong>
            </div>
            <div className="col-6">
              <small className="text-muted d-block">Total Amount</small>
              <strong>${parseFloat(stats.total_amount || 0).toFixed(2)}</strong>
            </div>
            <div className="col-6">
              <small className="text-muted d-block">Last Donation</small>
              <strong>{stats.last_donation_date || "N/A"}</strong>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <>
      <Helmet>
        <title> Donation - Sydney Sri Ayyappa Swami Centre</title>
        <meta
          name="description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta
          name="keywords"
          content="Sydney Sri Ayyappa Swami Centre, Ayyappa Temple Sydney, Lord Ayyappa Temple Australia, Hindu Temple in Sydney, Ayyappa Pooja Sydney, Swami Ayyappa Devotees Sydney, Sabarimala Ayyappa Sydney, Hindu Religious Centre Sydney, Ayyappa Bhajans Sydney, Indian Hindu Temple Sydney"
        />

        <meta property="og:title" content="Sydney Sri Ayyappa Swami Centre" />
        <meta
          property="og:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sydneysriayyappa.com/" />

        <meta
          property="og:see_also"
          content="https://www.youtube.com/@SydneySriAyyappaSwamiCentre"
        />
        <meta
          property="og:see_also"
          content="https://www.instagram.com/sydneysriayyappa/"
        />
        <meta
          property="og:see_also"
          content="https://www.facebook.com/sydneyayyappa"
        />

        <meta
          property="fb:admins"
          content="https://www.facebook.com/sydneyayyappa"
        />

        <meta name="instagram:title" content="Sydney Sri Ayyappa Swami Centre" />
        <meta
          name="instagram:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />

        <meta name="instagram:site" content="@sydneysriayyappa" />
      </Helmet>

      <div className="container-fluid p-0">
        <Banner bannerData={donationPageData} />

        <AnimatePresence>
          {alertMessage && <Alert message={alertMessage} type={alertType} />}
        </AnimatePresence>

        <section className="py-5" style={{ background: "#f8f9fa" }}>
          <div className="container">
            <motion.div
              className="text-center mb-5"
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="fw-bold mb-3 heading">Make a Donation</h1>
              <p className="text-muted fs-5 mx-auto" style={{ maxWidth: "760px" }}>
                Your generous contribution supports temple activities, daily
                poojas, community services, and special spiritual events.
              </p>
            </motion.div>

            <div
              className={`row g-4 ${
                formData.donation_mode === "bank_transfer"
                  ? "align-items-start"
                  : "align-items-stretch"
              }`}
            >
              <div className="col-lg-8 d-flex">
                <motion.div
                  className="card border-0 rounded-4 h-100 w-100"
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  <div className="card-body p-4 p-md-5 h-100 d-flex flex-column">
                    <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
                      <h4 className="fw-bold mb-4 heading">Donor Information</h4>

                      {addressError && (
                        <div className="alert alert-warning mb-3">
                          <i className="bi bi-exclamation-triangle-fill me-2"></i>
                          {addressError}
                        </div>
                      )}

                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            <i className="bi bi-envelope me-2 text-warning"></i>
                            Email <span className="text-danger">*</span>
                          </label>
                          <div className="position-relative">
                            <input
                              type="email"
                              name="email"
                              className="form-control form-control-lg"
                              placeholder="Enter email"
                              value={displayData.email}
                              onChange={handleChange}
                              onBlur={handleEmailBlur}
                              required
                            />
                            {isLookingUpDonor && (
                              <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                                <div
                                  className="spinner-border spinner-border-sm text-warning"
                                  role="status"
                                >
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            <i className="bi bi-telephone me-2 text-warning"></i>
                            Phone Number <span className="text-danger">*</span>
                          </label>
                          <input
                            type="tel"
                            name="phone_number"
                            className="form-control form-control-lg"
                            placeholder="Phone number"
                            value={displayData.phone_number}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            <i className="bi bi-person me-2 text-warning"></i>
                            First Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="first_name"
                            className="form-control form-control-lg"
                            placeholder="First name"
                            value={displayData.first_name}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            <i className="bi bi-person-badge me-2 text-warning"></i>
                            Last Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="last_name"
                            className="form-control form-control-lg"
                            placeholder="Last name"
                            value={displayData.last_name}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div className="col-12">
                          <label className="form-label fw-semibold">
                            <i className="bi bi-geo-alt me-2 text-warning"></i>
                            Address (Optional)
                          </label>
                          <input
                            ref={addressInputRef}
                            type="text"
                            name="address_input"
                            className="form-control form-control-lg"
                            placeholder="Enter your full address (optional - e.g., 123 Main Street, Sydney, NSW, 2000)"
                            onChange={handleChange}
                          />
                        </div>

                        <input type="hidden" name="street_no" value={formData.street_no} />
                        <input
                          type="hidden"
                          name="street_name"
                          value={formData.street_name}
                        />
                        <input type="hidden" name="suburb" value={formData.suburb} />
                        <input type="hidden" name="state" value={formData.state} />
                        <input
                          type="hidden"
                          name="postal_code"
                          value={formData.postal_code}
                        />
                        <input type="hidden" name="country" value={formData.country} />
                      </div>

                      <hr className="my-4" />

                      <h4 className="fw-bold mb-4 heading">Donation Details</h4>

                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            <i className="bi bi-heart me-2 text-warning"></i>
                            Donation For <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="donation_for"
                            className="form-control"
                            placeholder="e.g. Annadanam, Temple Maintenance"
                            value={formData.donation_for}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            <i className="bi bi-cash-coin me-2 text-warning"></i>
                            Amount (AUD) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="number"
                            name="amount"
                            className="form-control"
                            placeholder="Enter amount"
                            value={formData.amount}
                            onChange={handleChange}
                            min="0.50"
                            step="0.01"
                            required
                          />
                        </div>

                        <div className="col-12">
                          <label className="form-label fw-semibold">
                            <i className="bi bi-chat-left-text me-2 text-warning"></i>
                            Donation Details
                          </label>
                          <textarea
                            name="donation_details"
                            className="form-control"
                            rows="4"
                            placeholder="Write your donation note or purpose"
                            value={formData.donation_details}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-12">
                          <label className="form-label fw-semibold d-block mb-3">
                            <i className="bi bi-credit-card me-2 text-warning"></i>
                            Donation Mode <span className="text-danger">*</span>
                          </label>

                          <div className="row g-3">
                            {/* <div className="col-md-4">
                              <label className="card border rounded-4 p-3 h-100">
                                <div className="form-check m-0">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="donation_mode"
                                    value="paypal"
                                    checked={formData.donation_mode === "paypal"}
                                    onChange={handleChange}
                                  />
                                  <span className="form-check-label fw-semibold ms-2">
                                    <i className="bi bi-paypal text-warning me-1"></i>
                                    PayPal
                                  </span>
                                </div>
                              </label>
                            </div> */}

                            <div className="col-md-4">
                              <label className="card border rounded-4 p-3 h-100">
                                <div className="form-check m-0">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="donation_mode"
                                    value="stripe"
                                    checked={formData.donation_mode === "stripe"}
                                    onChange={handleChange}
                                  />
                                  <span className="form-check-label fw-semibold ms-2">
                                    <i className="bi bi-credit-card text-warning me-1"></i>
                                    Debit or Credit Card
                                  </span>
                                </div>
                              </label>
                            </div>

                            <div className="col-md-4">
                              <label className="card border rounded-4 p-3 h-100">
                                <div className="form-check m-0">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="donation_mode"
                                    value="bank_transfer"
                                    checked={formData.donation_mode === "bank_transfer"}
                                    onChange={handleChange}
                                  />
                                  <span className="form-check-label fw-semibold ms-2">
                                    <i className="bi bi-bank text-warning me-1"></i>
                                    Bank Transfer
                                  </span>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      <AnimatePresence>
                        {formData.donation_mode === "bank_transfer" && (
                          <motion.div
                            className="card border bg-light rounded-4 mt-4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.35 }}
                          >
                            <div className="card-body p-4">
                              <h5 className="fw-bold mb-3 heading">
                                Bank Transfer Details
                              </h5>
                              <p className="mb-2">
                                <strong>Bank:</strong> CBA
                              </p>
                              <p className="mb-2">
                                <strong>Account Name:</strong> Sydney Sri Ayyappa
                                Swami Centre Inc
                              </p>
                              <p className="mb-2">
                                <strong>BSB:</strong> 062-268
                              </p>
                              <p className="mb-2">
                                <strong>Account Number:</strong> 10465995
                              </p>
                              <p className="mb-0">
                                <strong>Reference:</strong> Full name
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <div className="mt-3">
                        {formData.donation_mode === "stripe" ? (
                          <div className="d-flex gap-3">
                            <button
                              type="submit"
                              className="btn btn-warning text-white fw-bold px-5 py-3 rounded-pill border-0"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Processing...
                                </>
                              ) : (
                                "Proceed Donation"
                              )}
                            </button>
                          </div>
                        ) : !formData.donation_mode ? (
                          <div className="text-muted text-center mt-3">
                            <p className="mb-0 text-warning fw-semibold">
                              Please select a payment method above
                            </p>
                          </div>
                        ) : null}
                      </div>
                    </form>
                  </div>
                </motion.div>
              </div>

              <div className="col-lg-4 d-flex">
                <div
                  className={`d-flex flex-column w-100 ${
                    formData.donation_mode === "bank_transfer" ? "" : "h-100"
                  }`}
                >
                  <motion.div
                    className="card border-0 rounded-4"
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <div className="card-body">
                      <h5 className="fw-bold mb-3 heading">Support Our Temple</h5>
                      <p className="text-muted mb-3">
                        Your contribution helps us maintain temple facilities,
                        conduct daily poojas, organize festivals, and support the
                        spiritual well-being of the community.
                      </p>
                      <div className="d-flex align-items-start mb-3">
                        <i className="bi bi-flower1 text-warning me-3 fs-5"></i>
                        <span className="text-muted">Daily poojas and rituals</span>
                      </div>
                      <div className="d-flex align-items-start mb-3">
                        <i className="bi bi-building text-warning me-3 fs-5"></i>
                        <span className="text-muted">Temple maintenance</span>
                      </div>
                      <div className="d-flex align-items-start mb-3">
                        <i className="bi bi-calendar-event text-warning me-3 fs-5"></i>
                        <span className="text-muted">
                          Festival and event support
                        </span>
                      </div>
                      <div className="d-flex align-items-start">
                        <i className="bi bi-people text-warning me-3 fs-5"></i>
                        <span className="text-muted">
                          Community service activities
                        </span>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    className={`mt-3 card border-0 rounded-4 d-flex ${
                      formData.donation_mode === "bank_transfer" ? "" : "flex-grow-1"
                    }`}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
                  >
                    <div
                      className={`card-body p-3 w-100 d-flex flex-column ${
                        formData.donation_mode === "bank_transfer" ? "" : "h-100"
                      }`}
                    >
                      <div
                        className={`d-flex flex-column ${
                          formData.donation_mode === "bank_transfer" ? "" : "h-100"
                        }`}
                      >
                        <div className="card contact-card mb-3">
                          <div className="card-body text-center p-3">
                            <div className="mb-3">
                              <i className="bi bi-gift text-warning display-5"></i>
                            </div>
                            <h6 className="mb-3 fw-bold">Material Donation</h6>
                            <p className="text-muted mb-0">
                              Flowers, fruits, and other items for daily prayers
                            </p>
                          </div>
                        </div>

                        <div className="card contact-card mb-3">
                          <div className="card-body text-center p-4">
                            <div className="mb-3">
                              <i className="bi bi-clock text-warning display-5"></i>
                            </div>
                            <h6 className="mb-3 fw-bold">Volunteer Time</h6>
                            <p className="text-muted mb-0">
                              Help with temple activities and events
                            </p>
                          </div>
                        </div>

                        <div
                          className={`card contact-card ${
                            formData.donation_mode === "bank_transfer"
                              ? "mt-3"
                              : "mt-auto"
                          }`}
                        >
                          <div className="card-body text-center p-4">
                            <div className="mb-3">
                              <i className="bi bi-people-fill text-warning display-5"></i>
                            </div>
                            <h6 className="mb-3 fw-bold">Prayer Requests</h6>
                            <p className="text-muted mb-0">
                              Special prayers for your family and loved ones
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="container py-5">
          <motion.div
            className="mt-5 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.6 }}
          >
            <div className="bg-light p-4 rounded">
              <h5 className="fw-bold mb-3 heading">Contact for More Information</h5>
              <p className="text-muted mb-3">
                If you have any questions about donations or would like to discuss
                specific ways to support our temple, please contact us.
              </p>
              <a
                href="/contactus"
                className="btn btn-warning px-4 py-2 hover-button text-white fw-bold border-0 position-relative rounded-5"
              >
                Contact Us
              </a>
            </div>
          </motion.div>
        </section>
      </div>

      <PayPalModal
        isOpen={isPayPalModalOpen}
        onClose={handlePayPalClose}
        onCancel={handlePayPalCancel}
        amount={pendingPayPalData?.amount}
        transactionId={pendingPayPalData?.transactionId}
        donorEmail={pendingPayPalData?.donorEmail}
        onSuccess={handlePayPalSuccess}
        onError={handlePayPalError}
      />

      <StripeElementsModal
        isOpen={isStripeModalOpen}
        onClose={() => {
          setIsStripeModalOpen(false);
          setPendingStripeData(null);
        }}
        onCancel={handleStripeCancel}
        onSuccess={handleStripeSuccess}
        onError={handleStripeError}
        amount={pendingStripeData?.amount}
        transactionId={pendingStripeData?.transactionId}
        donorEmail={pendingStripeData?.donorEmail}
        donorName={pendingStripeData?.donorName}
        clientSecret={pendingStripeData?.clientSecret}
        publishableKey={pendingStripeData?.publishableKey}
      />
    </>
  );
};

export default Donation;